/**
 * The Pending Review "what the rules will do" cells: Vend type path, Deeliver
 * category (or the unmapped hold), sell in app, 18+, tags and brand.
 *
 * Renders a fragment of <TableCell>s, placed between Name and Price. Pair it
 * with STAGED_ITEM_RULE_HEADERS for the header row.
 */
import React from 'react';
import { Chip, Stack, TableCell, Tooltip, Typography, alpha } from '@mui/material';
import { useAdminTheme } from '../../admin-theme';

export interface ResolvedCategorySummary {
  id: string | null;
  name: string | null;
  /** True when approval is held because the Vend type has no mapping. */
  held: boolean;
}

/** The staged-item fields these cells read (list rows from GET vaperite/staging/items). */
export interface StagedItemRuleFields {
  type?: 'new_item' | 'field_update' | 'removal';
  sellInApp?: boolean;
  isRestricted?: boolean;
  /** Vend type path, root first. Field-change-only rows send []. */
  categoryPath?: string[];
  resolvedCategory?: ResolvedCategorySummary | null;
  vendType?: string | null;
  tags?: string[];
  brand?: string | null;
  /**
   * The brand approval gives the product, through Brand Mappings (Jason, 29 Oct 2026), and how:
   * 'brand' = mapped to a Deeliver brand, 'none' = mapped to No brand, null = unmapped (as is).
   */
  resolvedBrand?: string | null;
  brandMapping?: 'brand' | 'none' | null;
}

export const STAGED_ITEM_RULE_HEADERS = ['Vend type', 'Category', 'Sell in app', '18+', 'Tags', 'Brand'] as const;

const Flag: React.FC<{ label: string; on: boolean }> = ({ label, on }) => {
  const { colors } = useAdminTheme();
  return (
    <Typography
      component="span"
      aria-label={`${label}: ${on ? 'yes' : 'no'}`}
      sx={{ fontWeight: 700, color: on ? colors.success : colors.textMuted }}
    >
      {on ? '✓' : '✗'}
    </Typography>
  );
};

const StagedItemRuleCells: React.FC<{ item: StagedItemRuleFields; visible?: Set<string> }> = ({ item, visible }) => {
  const { colors } = useAdminTheme();
  const path = item.categoryPath ?? [];
  const typePath = path.join(' > ');
  const category = item.resolvedCategory;
  // Field-change-only rows are existing products with nothing staged: the
  // backend sends the product's current category, not a resolution.
  const isCurrentCategory = item.type === 'field_update' && path.length === 0;
  const tags = item.tags ?? [];
  const show = (h: (typeof STAGED_ITEM_RULE_HEADERS)[number]) => !visible || visible.has(h);

  return (
    <>
      {show('Vend type') && <TableCell>
        <Typography variant="body2" sx={{ maxWidth: 220, fontSize: '0.75rem', color: colors.textSecondary }} noWrap title={typePath}>
          {typePath || '—'}
        </Typography>
      </TableCell>}
      {show('Category') && <TableCell>
        {category?.held ? (
          <Tooltip title={category.name ? `Vend type "${category.name}" has no category mapping` : 'No category mapping'}>
            <Chip
              label="Held: unmapped"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.62rem',
                fontWeight: 600,
                bgcolor: alpha(colors.warning, 0.12),
                color: colors.warning,
                border: `1px solid ${alpha(colors.warning, 0.3)}`,
              }}
            />
          </Tooltip>
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="baseline">
            <Typography variant="body2" sx={{ fontSize: '0.78rem' }} noWrap>
              {category?.name ?? '—'}
            </Typography>
            {isCurrentCategory && category?.name && (
              <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.62rem' }}>
                current
              </Typography>
            )}
          </Stack>
        )}
      </TableCell>}
      {show('Sell in app') && <TableCell align="center"><Flag label="Sell in app" on={item.sellInApp === true} /></TableCell>}
      {show('18+') && <TableCell align="center"><Flag label="18+" on={item.isRestricted === true} /></TableCell>}
      {show('Tags') && <TableCell>
        {tags.length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {tags.map((t) => (
              <Chip
                key={t}
                label={t}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: alpha(colors.purple, 0.12),
                  color: colors.purple,
                  border: `1px solid ${alpha(colors.purple, 0.25)}`,
                }}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: colors.textMuted }}>—</Typography>
        )}
      </TableCell>}
      {show('Brand') && <TableCell>
        {item.brandMapping ? (
          // Mapped on Brand Mappings: show what the product will get, with the Vend name on hover.
          <Tooltip title={`Vend brand "${item.brand ?? ''}" ${item.brandMapping === 'none' ? 'is mapped to No brand' : `maps to ${item.resolvedBrand}`}`}>
            <Typography variant="body2" sx={{ fontSize: '0.78rem', color: item.brandMapping === 'none' ? colors.textMuted : undefined }} noWrap>
              {item.brandMapping === 'none' ? 'No brand' : item.resolvedBrand}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ fontSize: '0.78rem' }} noWrap>
            {item.brand || '—'}
          </Typography>
        )}
      </TableCell>}
    </>
  );
};

export default StagedItemRuleCells;
